import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/biohackerBrand";

/**
 * SiteNav — unified marketing navigation for every public page.
 *
 * Order matches the IA agreed with the founder:
 *   HOME · PLATFORM · SOLUTIONS · SPORTS · TECHNOLOGY · ENTERPRISE · PRICING · RESOURCES · REQUEST DEMO
 */
const LINKS = [
  { to: "/",           label: "Home",       testid: "nav-home" },
  { to: "/platform",   label: "Platform",   testid: "nav-platform" },
  { to: "/solutions",  label: "Solutions",  testid: "nav-solutions" },
  { to: "/sports",     label: "Sports",     testid: "nav-sports" },
  { to: "/technology", label: "Technology", testid: "nav-technology" },
  { to: "/enterprise", label: "Enterprise", testid: "nav-enterprise" },
  { to: "/fusion",     label: "Fusion",     testid: "nav-fusion" },
  { to: "/pricing",    label: "Pricing",    testid: "nav-pricing" },
  { to: "/resources",  label: "Resources",  testid: "nav-resources" },
];

export default function SiteNav({ dark = true }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className={dark ? "absolute top-0 left-0 right-0 z-30" : "sticky top-0 z-30 bg-white/[0.02] backdrop-blur-md border-b border-white/8"}
      data-testid="site-nav">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
        <Logo size="md" variant="lockup" linkTo="/" dark />
        <nav className="hidden lg:flex items-center gap-6 text-[13px] text-white/70">
          {LINKS.map((l) => {
            const active = pathname === l.to || (l.to !== "/" && pathname.startsWith(l.to));
            return (
              <Link key={l.to} to={l.to} data-testid={l.testid}
                className={`transition-colors ${active ? "text-white" : "hover:text-white"}`}>
                {l.label}
              </Link>
            );
          })}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <Link to="/login" className="text-[13px] text-white/70 hover:text-white transition-colors" data-testid="nav-login">
            Sign in
          </Link>
          <Link to="/request-demo" data-testid="nav-cta">
            <Button className="text-white font-bold text-xs px-4 py-2"
              style={{ backgroundColor: "var(--brand-secondary)" }}>
              Request demo
            </Button>
          </Link>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="lg:hidden text-white/80" data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-[#050810] border-t border-white/8 px-6 py-4 space-y-3"
          data-testid="nav-mobile-panel">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block text-sm text-white/80 hover:text-white" data-testid={`${l.testid}-mobile`}>
              {l.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-white/8 flex flex-col gap-2">
            <Link to="/login" onClick={() => setOpen(false)}
              className="text-sm text-white/80 hover:text-white">Sign in</Link>
            <Link to="/request-demo" onClick={() => setOpen(false)}>
              <Button className="w-full text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Request demo
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
